import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TextField from '../SanitizedTextField/SanitizedTextField';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { getDiscoCompanyMenuItems } from '../AdminCompanySelect/DiscoCompanyMenuItems';
import { ADMIN_COMPANY_SELECT_OPTIONS } from '../../constants/discoCompanies';
import './SearchPopup.scss';

export const SEARCH_SERVICE_OPTIONS = [
  { id: 1, label: 'New Connection', prefix: 'NC-' },
  { id: 5, label: 'Meter Change Order', prefix: 'MC-' },
  { id: 7, label: 'Due Date Extension', prefix: 'DE-', idLength: 18 },
  { id: 8, label: 'Bill Installments', prefix: 'BI-', idLength: 18 },
  { id: 11, label: 'Meter Reading Correction', prefix: 'MRC-', idLength: 19 },
  { id: 13, label: 'Reconnection', prefix: 'RC-' },
  { id: 15, label: 'Change of Attributes', prefix: 'CA-' },
  { id: 16, label: 'Adjust Last Bill', prefix: 'BA-', idLength: 17 },
  { id: 17, label: 'Part Payment', prefix: 'PP-', idLength: 17 },
  { id: 18, label: 'Advance Payment', prefix: 'AP-', idLength: 17 },
  { id: 19, label: 'Withdrawal of Installment', prefix: 'IWD-', idLength: 19 },
];

export const DEFAULT_SEARCH_SERVICE_TYPE = '1';

const SEARCH_PATH_SERVICE_TYPE = {
  'new-connection-table': '1',
  'new-connection-form': '1',
  'meter-change-request-table': '5',
  'meter-change-request-form': '5',
  'meter-change-request-update': '5',
  'due-date-extension-table': '7',
  'due-date-extension-form': '7',
  'installments-table': '8',
  'installments-form': '8',
  'meter-reading-correction-table': '11',
  'meter-reading-correction-form': '11',
  'mrc-bill': '11',
  'reconnection-table': '13',
  'reconnection-form': '13',
  'reconnection-update': '13',
  'change-of-attributes-table': '15',
  'change-of-attributes-form': '15',
  'change-of-attributes-update': '15',
  'adjust-last-bill-table': '16',
  'adjust-last-bill-form': '16',
  'adjust-last-bill-update': '16',
  'amend-2636-bill-table': '17',
  'amend-2636-bill-form': '17',
  'part-payment-update': '17',
  'advance-payment-table': '18',
  'advance-payment-form': '18',
  'advance-payment-update': '18',
  'advance-payment-bill': '18',
  'withdrawal-of-installment-table': '19',
  'withdrawal-of-installment-form': '19',
};

export const getSearchServiceTypeFromPath = (pathname = '') => {
  const segments = String(pathname || '')
    .toLowerCase()
    .split('/')
    .filter(Boolean);
  for (const segment of segments) {
    if (SEARCH_PATH_SERVICE_TYPE[segment]) {
      return SEARCH_PATH_SERVICE_TYPE[segment];
    }
  }
  return DEFAULT_SEARCH_SERVICE_TYPE;
};

export const SEARCH_APPLICATION_ID_NUMERIC_LENGTH = 14;
export const SEARCH_APPLICATION_ID_LONG_NUMERIC_LENGTH = 15;

const SEARCH_APPLICATION_ID_TOTAL_LENGTH_BY_LETTERS =
  SEARCH_SERVICE_OPTIONS.reduce((acc, item) => {
    if (item.idLength) {
      const letters = String(item.prefix || '')
        .replace(/-/g, '')
        .toUpperCase();
      if (letters) acc[letters] = item.idLength;
    }
    return acc;
  }, {});

const getServiceLettersFromPrefix = (prefix = '') =>
  (String(prefix).match(/^[A-Za-z]+/) || [''])[0].toUpperCase();

/** Numeric digits after the service prefix. Total ID length includes prefix. */
export const getSearchApplicationIdNumericLengthFromPrefix = (prefix = '') => {
  const letters = getServiceLettersFromPrefix(prefix);
  const hyphenPrefix = letters ? `${letters}-` : '';
  const totalLength = SEARCH_APPLICATION_ID_TOTAL_LENGTH_BY_LETTERS[letters];
  if (totalLength) return Math.max(totalLength - hyphenPrefix.length, 1);
  return letters.length >= 3
    ? SEARCH_APPLICATION_ID_LONG_NUMERIC_LENGTH
    : SEARCH_APPLICATION_ID_NUMERIC_LENGTH;
};

export const getSearchApplicationIdNumericLength = (typeId) =>
  getSearchApplicationIdNumericLengthFromPrefix(getSearchServicePrefix(typeId));

export const getSearchApplicationIdOfficeDigits = ({
  isAdmin = false,
  officeCode = '',
  hierarchy,
  selectedCompanyCode = '',
} = {}) => {
  if (isAdmin) {
    return String(selectedCompanyCode || '').replace(/\D/g, '').slice(0, 2);
  }

  const code = String(officeCode || '').replace(/\D/g, '');
  const level = Number(hierarchy);

  if (level === 1) return code.slice(0, 2);
  if (level === 2) return code.slice(0, 3);
  if (level === 3) return code.slice(0, 4);
  if (level === 4 || level === 5) return code.slice(0, 5);

  if (code.length >= 5) return code.slice(0, 5);
  if (code.length === 4) return code.slice(0, 4);
  if (code.length === 3) return code.slice(0, 3);
  return code.slice(0, 2);
};

export const getSearchServicePrefix = (typeId) => {
  const match = SEARCH_SERVICE_OPTIONS.find((item) => String(item.id) === String(typeId));
  return match?.prefix || '';
};

export const getSearchApplicationIdRemainingLength = (officeContext = {}, typeId) => {
  const officeDigits = getSearchApplicationIdOfficeDigits(officeContext);
  const numericLength = getSearchApplicationIdNumericLength(typeId);
  return Math.max(numericLength - officeDigits.length, 1);
};

export const getSearchApplicationIdPrefix = (typeId, officeContext = {}) => {
  const servicePrefix = getSearchServicePrefix(typeId);
  if (!servicePrefix) return '';
  const officeDigits = getSearchApplicationIdOfficeDigits(officeContext);
  return `${servicePrefix}${officeDigits}`;
};

export const PASTED_APPLICATION_COMPANY_MISMATCH_MESSAGE =
  'Your pasted application does not belong to this company.';

export const getApplicationIdCompanyDigits = (value) => {
  let next = String(value || '').replace(/\s/g, '').toUpperCase();
  next = next.replace(/^[A-Z]{2,4}-/, '');
  return next.replace(/[^0-9]/g, '').slice(0, 2);
};

export const isFullApplicationIdPaste = (value, prefix = '') => {
  const raw = String(value || '').replace(/\s/g, '').toUpperCase();
  if (/^[A-Z]{2,4}-/.test(raw)) return true;
  const digits = raw.replace(/[^0-9]/g, '');
  const numericLength = getSearchApplicationIdNumericLengthFromPrefix(prefix);
  return digits.length >= numericLength;
};

export const doesPastedApplicationBelongToCompany = (value, prefix = '') => {
  const officeDigits = String(prefix).replace(/^[A-Za-z]+-/, '');
  const expectedCompany = officeDigits.slice(0, 2);
  if (expectedCompany.length < 2) return true;
  if (!isFullApplicationIdPaste(value, prefix)) return true;
  const pastedCompany = getApplicationIdCompanyDigits(value);
  if (pastedCompany.length < 2) return true;
  return pastedCompany === expectedCompany;
};

export const stripSearchApplicationIdPrefix = (value, prefix = '') => {
  let next = String(value || '').replace(/\s/g, '').toUpperCase();
  const normalizedPrefix = String(prefix || '').toUpperCase();
  const officeDigits = String(prefix).replace(/^[A-Za-z]+-/, '');
  const hadServicePrefix = /^[A-Z]{2,4}-/.test(next);
  const numericLength = getSearchApplicationIdNumericLengthFromPrefix(prefix);

  if (normalizedPrefix && next.startsWith(normalizedPrefix)) {
    return next.slice(normalizedPrefix.length).replace(/[^0-9]/g, '');
  }

  next = next.replace(/^[A-Z]{2,4}-/, '');
  const digits = next.replace(/[^0-9]/g, '');
  if (!officeDigits) return digits;

  const remainingLength = Math.max(numericLength - officeDigits.length, 1);
  // Strip office digits only for a full application ID. Remaining digits often
  // start with the same office code (NC-13 + 131260909004), so do not strip
  // just because the typed suffix starts with those digits.
  if (hadServicePrefix || digits.length >= numericLength) {
    return digits.slice(officeDigits.length);
  }

  if (digits.length > remainingLength && digits.length - officeDigits.length === remainingLength) {
    return digits.slice(officeDigits.length);
  }

  return digits;
};

export const normalizeSearchApplicationIdInput = (value, prefix = '', maxLength) => {
  const suffix = stripSearchApplicationIdPrefix(value, prefix);
  if (!maxLength) return suffix;
  return suffix.slice(0, maxLength);
};

export const composeSearchApplicationId = (typeId, enteredId, officeContext = {}) => {
  const prefix = getSearchApplicationIdPrefix(typeId, officeContext);
  const suffix = stripSearchApplicationIdPrefix(enteredId, prefix);
  if (!suffix) return '';
  return `${prefix}${suffix}`;
};

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    padding: '0px 14px',
  },
};

const noBrowserAutocomplete = {
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'off',
  spellCheck: 'false',
  readOnly: true,
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-form-type': 'other',
};

const unlockAutocomplete = (event) => {
  event.target.removeAttribute('readonly');
};

const SearchPopup = ({
  searchBarrPopupOpen,
  handleCloseSearchBarPopup,
  selectedTab,
  handleTabChange,
  tabLabels,
  searchFormik,
  isAdmin = false,
}) => {
  const { code: officeCode, hierarchy } = useSelector((state) => state.auth);
  const [applicationIdPasteError, setApplicationIdPasteError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tabCount = tabLabels.length;

  const goToPrevTab = () => {
    const next = selectedTab <= 0 ? tabCount - 1 : selectedTab - 1;
    handleTabChange(null, next);
  };

  const goToNextTab = () => {
    const next = selectedTab >= tabCount - 1 ? 0 : selectedTab + 1;
    handleTabChange(null, next);
  };

  const fieldError =
    applicationIdPasteError ||
    (selectedTab === 0 && searchFormik.touched.iv_reference_no && searchFormik.errors.iv_reference_no) ||
    (selectedTab === 1 && searchFormik.touched.iv_application_id && searchFormik.errors.iv_application_id) ||
    (selectedTab === 2 && searchFormik.touched.iv_cnic && searchFormik.errors.iv_cnic) ||
    (selectedTab === 3 && searchFormik.touched.iv_applicant_name && searchFormik.errors.iv_applicant_name) ||
    '';
  const serviceError =
    searchFormik.touched.iv_application_type && searchFormik.errors.iv_application_type
      ? searchFormik.errors.iv_application_type
      : '';
  const companyError =
    isAdmin &&
      searchFormik.touched.iv_company_code &&
      searchFormik.errors.iv_company_code
      ? searchFormik.errors.iv_company_code
      : '';
  const hasService = Boolean(searchFormik.values.iv_application_type);
  const hasCompany = Boolean(searchFormik.values.iv_company_code);
  const canSubmit = hasService && (!isAdmin || hasCompany);
  const applicationIdOfficeContext = {
    isAdmin,
    officeCode,
    hierarchy,
    selectedCompanyCode: searchFormik.values.iv_company_code,
  };
  const applicationIdPrefix = getSearchApplicationIdPrefix(
    searchFormik.values.iv_application_type,
    applicationIdOfficeContext,
  );
  const applicationIdRemainingLength = getSearchApplicationIdRemainingLength(
    applicationIdOfficeContext,
    searchFormik.values.iv_application_type,
  );

  const handleApplicationIdChange = (event) => {
    setApplicationIdPasteError('');
    const nextValue = normalizeSearchApplicationIdInput(
      event.target.value,
      applicationIdPrefix,
      applicationIdRemainingLength,
    );
    searchFormik.setFieldValue('iv_application_id', nextValue, true);
  };

  const handleApplicationIdPaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    if (!doesPastedApplicationBelongToCompany(pasted, applicationIdPrefix)) {
      searchFormik.setFieldValue('iv_application_id', '', false);
      searchFormik.setFieldTouched('iv_application_id', true, false);
      setApplicationIdPasteError(PASTED_APPLICATION_COMPANY_MISMATCH_MESSAGE);
      return;
    }
    setApplicationIdPasteError('');
    const nextValue = normalizeSearchApplicationIdInput(
      pasted,
      applicationIdPrefix,
      applicationIdRemainingLength,
    );
    searchFormik.setFieldValue('iv_application_id', nextValue);
  };

  const renderField = () => {
    if (selectedTab === 0) {
      return (
        <TextField
          fullWidth
          variant="outlined"
          name="iv_reference_no"
          id="iv_reference_no"
          placeholder="Enter reference number"
          value={searchFormik.values.iv_reference_no}
          onChange={searchFormik.handleChange}
          onBlur={searchFormik.handleBlur}
          autoComplete="off"
          onFocus={unlockAutocomplete}
          inputProps={{ ...noBrowserAutocomplete, maxLength: 14 }}
          sx={textFieldSx}
        />
      );
    }
    if (selectedTab === 1) {
      const suffixMaxLength = applicationIdPrefix
        ? applicationIdRemainingLength
        : getSearchApplicationIdNumericLength(searchFormik.values.iv_application_type);
      return (
        <div className={`search-popup__id-input${applicationIdPrefix ? ' has-prefix' : ''}`}>
          {applicationIdPrefix ? (
            <span className="search-popup__id-prefix">{applicationIdPrefix}</span>
          ) : null}
          <TextField
            fullWidth
            variant="outlined"
            placeholder={
              applicationIdPrefix
                ? `Enter ${suffixMaxLength}-digit ID`
                : 'Enter application ID'
            }
            name="iv_application_id"
            id="iv_application_id"
            value={searchFormik.values.iv_application_id}
            onChange={handleApplicationIdChange}
            onPaste={handleApplicationIdPaste}
            onBlur={searchFormik.handleBlur}
            autoComplete="off"
            onFocus={unlockAutocomplete}
            inputProps={{ ...noBrowserAutocomplete, maxLength: suffixMaxLength }}
            sx={textFieldSx}
          />
        </div>
      );
    }
    if (selectedTab === 2) {
      return (
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Enter CNIC"
          name="iv_cnic"
          id="iv_cnic"
          value={searchFormik.values.iv_cnic}
          onChange={searchFormik.handleChange}
          onBlur={searchFormik.handleBlur}
          autoComplete="off"
          onFocus={unlockAutocomplete}
          inputProps={{ ...noBrowserAutocomplete, maxLength: 13 }}
          sx={textFieldSx}
        />
      );
    }
    return (
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Enter applicant name"
        name="iv_applicant_name"
        id="iv_applicant_name"
        value={searchFormik.values.iv_applicant_name}
        onChange={searchFormik.handleChange}
        onBlur={searchFormik.handleBlur}
        autoComplete="off"
        onFocus={unlockAutocomplete}
        inputProps={{ ...noBrowserAutocomplete, minLength: 3 }}
        sx={textFieldSx}
      />
    );
  };

  return (
    <Dialog
      open={searchBarrPopupOpen}
      onClose={handleCloseSearchBarPopup}
      maxWidth={isMobile ? false : 'md'}
      fullWidth={isMobile}
      className={`search-popup${isMobile ? ' search-popup--mobile' : ''}${isAdmin ? ' search-popup--admin' : ''}`}
      sx={
        isMobile
          ? {
            '& .MuiDialog-paper': {
              borderRadius: '12px',
            },
          }
          : {
            '& .MuiDialog-container': {
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              padding: '72px 24px 24px',
            },
            '& .MuiDialog-paper': {
              margin: 0,
              borderRadius: '14px',
              width: 'auto',
              minWidth: `min(${isAdmin ? 760 : 620}px, calc(100vw - 48px))`,
              maxWidth: `min(${isAdmin ? 820 : 680}px, calc(100vw - 48px))`,
            },
          }
      }
    >
      <DialogContent className="search-popup__content">
        <div className="search-popup__heading">
          <span className="search-popup__heading-title">Search Application</span>
          <span className="search-popup__heading-hint">
            {isAdmin ? 'Please select a company and a service.' : 'Please select a service.'}
          </span>
        </div>
        {isMobile ? (
          <div className="search-popup__tab-nav" role="tablist" aria-label="Search type">
            <IconButton
              type="button"
              className="search-popup__tab-nav-btn"
              onClick={goToPrevTab}
              aria-label="Previous search type"
              size="small"
            >
              <ChevronLeftIcon fontSize="medium" />
            </IconButton>
            <button
              type="button"
              className="search-popup__tab-nav-label"
              onClick={goToNextTab}
              aria-label={`Current: ${tabLabels[selectedTab]}. Tap for next.`}
            >
              {tabLabels[selectedTab]}
            </button>
            <IconButton
              type="button"
              className="search-popup__tab-nav-btn"
              onClick={goToNextTab}
              aria-label="Next search type"
              size="small"
            >
              <ChevronRightIcon fontSize="medium" />
            </IconButton>
          </div>
        ) : (
          <div className="search-popup__tabs-wrap">
            <Tabs
              className="search-popup__tabs"
              value={selectedTab}
              onChange={handleTabChange}
              textColor="inherit"
              variant="standard"
            >
              {tabLabels.map((label, index) => (
                <Tab key={index} label={label} />
              ))}
            </Tabs>
          </div>
        )}
        <form
          onSubmit={searchFormik.handleSubmit}
          className="search-popup__form"
          autoComplete="off"
        >
          <input
            type="text"
            name="search_popup_autofill_trap"
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            className="search-popup__autofill-trap"
          />
          <div className="search-popup__bar">
            {isAdmin ? (
              <div className={`search-popup__service search-popup__company${companyError ? ' is-error' : ''}${hasCompany ? '' : ' is-empty'}`}>
                <CorporateFareOutlinedIcon className="search-popup__service-icon" />
                <Select
                  id="iv_company_code"
                  name="iv_company_code"
                  value={searchFormik.values.iv_company_code || ''}
                  onChange={(event) => {
                    setApplicationIdPasteError('');
                    searchFormik.handleChange(event);
                  }}
                  onBlur={searchFormik.handleBlur}
                  displayEmpty
                  variant="standard"
                  disableUnderline
                  className="search-popup__service-select"
                  renderValue={(selected) => {
                    if (!selected) return 'Select company *';
                    const match = ADMIN_COMPANY_SELECT_OPTIONS.find(
                      (item) => String(item.value) === String(selected)
                    );
                    return match?.label || 'Select company *';
                  }}
                  MenuProps={{
                    PaperProps: { className: 'search-popup__service-menu' },
                  }}
                >
                  <MenuItem value="">
                    <em>Select company *</em>
                  </MenuItem>
                  {getDiscoCompanyMenuItems(
                    searchFormik.values.iv_company_code,
                    true,
                    false
                  )}
                </Select>
              </div>
            ) : null}
            <div className={`search-popup__service${serviceError ? ' is-error' : ''}${hasService ? '' : ' is-empty'}`}>
              <AppsRoundedIcon className="search-popup__service-icon" />
              <Select
                id="iv_application_type"
                name="iv_application_type"
                value={searchFormik.values.iv_application_type}
                onChange={searchFormik.handleChange}
                onBlur={searchFormik.handleBlur}
                displayEmpty
                variant="standard"
                disableUnderline
                className="search-popup__service-select"
                renderValue={(selected) => {
                  if (!selected) return 'Select service *';
                  const match = SEARCH_SERVICE_OPTIONS.find(
                    (item) => String(item.id) === String(selected)
                  );
                  return match?.label || 'Select service *';
                }}
                MenuProps={{
                  PaperProps: { className: 'search-popup__service-menu' },
                }}
              >
                {SEARCH_SERVICE_OPTIONS.map((item) => (
                  <MenuItem key={item.id} value={String(item.id)}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className="search-popup__field">{renderField()}</div>
            <button
              type="submit"
              className="search-popup__submit-btn"
              disabled={!canSubmit}
              onClick={() => {
                if (isAdmin && !hasCompany) {
                  searchFormik.setFieldTouched('iv_company_code', true, true);
                }
                if (!hasService) {
                  searchFormik.setFieldTouched('iv_application_type', true, true);
                }
              }}
            >
              <SearchRoundedIcon />
              Go
            </button>
          </div>
          {(companyError || serviceError || fieldError) && (
            <p className="search-popup__error">{companyError || serviceError || fieldError}</p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SearchPopup;
